import {
  ChallengeAddLabelButton,
  ChallengeLabelsFooterRow,
  ChallengeSaveButton,
  ChallengeSection,
  ChallengeSectionLabel,
  SavingSpinner,
} from '@/components/challenge/components';
import { ChallengeLabelRow } from '@/components/challenge/CompanyChallengeManagement/ChallengeLabelsSection/ChallengeLabelRow';
import { MAX_LABELS } from '@/components/challenge/CompanyChallengeManagement/constants';
import { IChallengeFormValues } from '@/components/challenge/CompanyChallengeManagement/types';
import {
  scrollNearestScrollbarToBottom,
  updateNearestScrollbar,
} from '@/utils/scrollbarHelpers';
import { Box } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';
import { FC, MutableRefObject, useLayoutEffect, useRef } from 'react';

interface ChallengeLabelsSectionProps {
  maxUsedLabelIndexRef: MutableRefObject<number>;
}

export const ChallengeLabelsSection: FC<ChallengeLabelsSectionProps> = ({
  maxUsedLabelIndexRef,
}) => {
  const { values, isSubmitting } = useFormikContext<IChallengeFormValues>();
  const { labels } = values;

  const footerRef = useRef<HTMLDivElement>(null);
  const previousLabelsLengthRef = useRef(labels.length);

  useLayoutEffect(() => {
    const previousLength = previousLabelsLengthRef.current;
    previousLabelsLengthRef.current = labels.length;

    if (labels.length > previousLength) {
      scrollNearestScrollbarToBottom(footerRef.current);
    }
    if (labels.length < previousLength) {
      updateNearestScrollbar(footerRef.current);
    }
  }, [labels.length]);

  return (
    <ChallengeSection>
      <ChallengeSectionLabel variant="h3">
        Labels (1-{MAX_LABELS})
      </ChallengeSectionLabel>
      <FieldArray name="labels">
        {({ push, remove }) => (
          <Box>
            {labels.map((label, displayIndex) => (
              <ChallengeLabelRow
                key={label.index}
                label={label}
                displayIndex={displayIndex}
                canRemove={labels.length > 1}
                onRemove={() => remove(displayIndex)}
              />
            ))}

            <ChallengeLabelsFooterRow ref={footerRef}>
              {labels.length < MAX_LABELS && (
                <ChallengeAddLabelButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    maxUsedLabelIndexRef.current += 1;
                    push({
                      index: maxUsedLabelIndexRef.current,
                      label: '',
                    });
                  }}
                >
                  + Add label
                </ChallengeAddLabelButton>
              )}
              <ChallengeSaveButton type="submit" disabled={isSubmitting}>
                {isSubmitting && <SavingSpinner />}
                {isSubmitting ? 'Saving Changes...' : 'Save Changes'}
              </ChallengeSaveButton>
            </ChallengeLabelsFooterRow>
          </Box>
        )}
      </FieldArray>
    </ChallengeSection>
  );
};
